<script setup>
/* global axios */
// Modal de Webhooks do Kanban — dispara POST externo em ações (card criado/movido/
// atualizado/removido), com assinatura HMAC opcional. Porta a WebhooksConfig do original.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const webhooks = ref([]);
const events = ref([]);
const loading = ref(true);
const showForm = ref(false);
const editing = ref(null);
const form = ref({ name: '', url: '', secret: '', events: [], is_active: true });

const load = async () => {
  loading.value = true;
  const { data } = await axios.get(`${base}/webhooks`);
  webhooks.value = data.data || [];
  events.value = data.events || [];
  loading.value = false;
};
const openNew = () => {
  editing.value = null;
  form.value = { name: '', url: '', secret: '', events: [], is_active: true };
  showForm.value = true;
};
const openEdit = w => {
  editing.value = w;
  form.value = {
    name: w.name,
    url: w.url,
    secret: w.secret || '',
    events: [...(w.events || [])],
    is_active: w.is_active,
  };
  showForm.value = true;
};
const toggleEvent = ev => {
  const i = form.value.events.indexOf(ev);
  if (i >= 0) form.value.events.splice(i, 1);
  else form.value.events.push(ev);
};
const save = async () => {
  if (!form.value.name.trim() || !form.value.url.trim()) {
    useAlert(t('KANBAN.webhooks.form.required'));
    return;
  }
  if (!form.value.events.length) {
    useAlert(t('KANBAN.webhooks.form.eventsRequired'));
    return;
  }
  if (editing.value)
    await axios.patch(`${base}/webhooks/${editing.value.id}`, form.value);
  else await axios.post(`${base}/webhooks`, form.value);
  showForm.value = false;
  await load();
};
const toggleActive = async w => {
  await axios.patch(`${base}/webhooks/${w.id}`, { is_active: !w.is_active });
  await load();
};
const test = async w => {
  await axios.post(`${base}/webhooks/${w.id}/test`);
  useAlert(t('KANBAN.webhooks.testSent'));
};
const remove = async w => {
  if (!window.confirm(t('KANBAN.webhooks.confirmDelete', { name: w.name })))
    return;
  await axios.delete(`${base}/webhooks/${w.id}`);
  await load();
};

onMounted(load);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
    @click.self="emit('close')"
  >
    <div
      class="w-[640px] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
      >
        <div>
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('KANBAN.webhooks.title') }}
          </h2>
          <p class="text-xs text-n-slate-10">
            {{ t('KANBAN.webhooks.subtitle') }}
          </p>
        </div>
        <button
          class="text-n-slate-10 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <!-- lista -->
        <template v-if="!showForm">
          <div class="flex justify-end mb-4">
            <button
              class="inline-flex items-center gap-1.5 px-3.5 h-9 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="openNew"
            >
              <span class="i-lucide-plus size-4" />
              {{ t('KANBAN.webhooks.newWebhook') }}
            </button>
          </div>
          <div v-if="loading" class="text-n-slate-11">
            {{ t('KANBAN.webhooks.loading') }}
          </div>
          <div
            v-else-if="!webhooks.length"
            class="py-10 text-center text-n-slate-10"
          >
            {{ t('KANBAN.webhooks.empty') }}
          </div>
          <div
            v-for="w in webhooks"
            :key="w.id"
            class="p-3.5 mb-2 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <div class="flex items-center gap-2">
              <span
                class="w-2 h-2 rounded-full shrink-0"
                :class="w.is_active ? 'bg-green-500' : 'bg-n-slate-6'"
              />
              <span class="font-medium text-n-slate-12 truncate">{{
                w.name
              }}</span>
              <span
                v-if="w.secret"
                class="px-1.5 py-0.5 text-[0.5625rem] rounded-full bg-n-alpha-2 text-n-slate-11"
                >HMAC</span
              >
              <div class="ml-auto flex items-center gap-1">
                <button
                  class="inline-flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                  :title="t('KANBAN.webhooks.actions.test')"
                  @click="test(w)"
                >
                  <span class="i-lucide-send size-4" />
                </button>
                <button
                  class="inline-flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                  :title="
                    w.is_active
                      ? t('KANBAN.webhooks.actions.deactivate')
                      : t('KANBAN.webhooks.actions.activate')
                  "
                  @click="toggleActive(w)"
                >
                  <span
                    :class="w.is_active ? 'i-lucide-pause' : 'i-lucide-play'"
                    class="size-4"
                  />
                </button>
                <button
                  class="inline-flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:bg-n-alpha-2"
                  :title="t('KANBAN.webhooks.actions.edit')"
                  @click="openEdit(w)"
                >
                  <span class="i-lucide-pencil size-4" />
                </button>
                <button
                  class="inline-flex items-center justify-center size-8 rounded-md text-n-ruby-11 hover:bg-n-alpha-2"
                  :title="t('KANBAN.webhooks.actions.delete')"
                  @click="remove(w)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
            <div class="mt-1 text-xs text-n-slate-10 truncate">{{ w.url }}</div>
            <div class="flex flex-wrap gap-1 mt-2">
              <span
                v-for="ev in w.events"
                :key="ev"
                class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-alpha-1 text-n-slate-11"
                >{{ ev }}</span
              >
            </div>
          </div>
        </template>

        <!-- form -->
        <template v-else>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.webhooks.form.name')
          }}</label>
          <input
            v-model="form.name"
            class="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('KANBAN.webhooks.form.namePlaceholder')"
          />
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.webhooks.form.url')
          }}</label>
          <input
            v-model="form.url"
            class="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            placeholder="https://n8n.exemplo.com/webhook/abc123"
          />
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.webhooks.form.secret')
          }}</label>
          <input
            v-model="form.secret"
            class="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('KANBAN.webhooks.form.secretPlaceholder')"
          />
          <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
            t('KANBAN.webhooks.form.events')
          }}</label>
          <div class="grid grid-cols-2 gap-2 mb-3">
            <label
              v-for="ev in events"
              :key="ev.value"
              class="flex items-center gap-2 p-2 text-sm rounded-lg border cursor-pointer"
              :class="
                form.events.includes(ev.value)
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak hover:border-n-slate-6'
              "
            >
              <input
                type="checkbox"
                :checked="form.events.includes(ev.value)"
                @change="toggleEvent(ev.value)"
              />
              <span class="text-n-slate-12">{{ ev.label }}</span>
            </label>
          </div>
          <label class="flex items-center gap-2 mb-4 text-sm text-n-slate-12">
            <input v-model="form.is_active" type="checkbox" />
            {{ t('KANBAN.webhooks.form.active') }}
          </label>
          <div class="flex justify-end gap-2">
            <button
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
              @click="showForm = false"
            >
              {{ t('KANBAN.webhooks.form.cancel') }}
            </button>
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="save"
            >
              {{ t('KANBAN.webhooks.form.save') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
